import { EChartsOption, SeriesOption } from 'echarts';

import { EnergyDetailsSeries } from '../../model';
import { getStyleProp, telemetryStyles } from '../../styles.utils';
import { coreServicesLabels } from '../../../../core-services.service';

export const buildMonthOverview = (
  series: EnergyDetailsSeries[],
  dates: string[],
): ((
  serieName: string[],
  resume: { serieName: string; description: string; showTotalMaxValue?: boolean },
  withNegativeSide?: boolean,
) => EChartsOption) => {
  return (serieNames, resume, withNegativeSide = true) => ({
    title: {
      text: `${ Math.abs(Number(series.find((serie) => serie.name === resume.serieName)?.values.flat()
        .filter((value): value is number => value !== null)
        .reduce((prev, value) => prev + value, 0)
        .toFixed())) } kWh - ${resume.description}`,
      left: 75,
      top: 20,
      textStyle: {
        color: getStyleProp('--grey-400'),
        fontSize: 16,
        fontFamily: getStyleProp('--font-family-brand'),
        fontWeight: 500,
      },
      subtextStyle: {
        fontSize: 12,
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      confine: true,
      trigger: 'axis',
      formatter: (elements: any): string => {
        const { dataIndex } = elements[0];
        const date = dates[dataIndex].split('-').reverse().join('/');
        let template = `
        <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
          <div class="body-3 mr-3">${coreServicesLabels.day}</div>
          <div class="text-mono">${date}</div>
        </div>`;
        for (const element of elements) {
          const {
            seriesName,
            data: { value },
          } = element;
          const { name, color } = telemetryStyles(seriesName);
          if (value !== 0) {
            template += `
            <div class="fx-row ${color} fx-align-center fx-justify-between">
              <div class="body-3 mr-3">${name}</div>
              <div class="text-mono">${Math.abs(value).toFixed(2)} kWh</div>
            </div>`;
          }
        }
        return template;
      },
      backgroundColor: getStyleProp('--grey-100'),
      borderColor: getStyleProp('--grey-100'),
      borderRadius: 4,
      textStyle: {
        color: getStyleProp('--white'),
      },
    },
    xAxis: {
      type : 'category',
      data: dates.map((date) => {
        const dateLabel = new Date(date)
        return `${dateLabel.getDate()}/${dateLabel.getMonth() + 1}`}),
    },
    grid: {
      show: false,
      top: '25%',
      bottom: '15%',
      left: '12%',
      right: '2%',
    },
    yAxis: {
      max: ({ max, min }) =>
        Math.abs(max) >= Math.abs(min) ? Math.abs(max) : Math.abs(min),
      min: ({ max, min }) =>
        withNegativeSide
          ? -(Math.abs(max) >= Math.abs(min) ? Math.abs(max) : Math.abs(min))
          : 0,
      type: 'value',
      name: '[kWh]',
      nameLocation: 'end',
      nameTextStyle: {
        color: getStyleProp('--grey-500'),
        fontWeight: 'bold',
      },
      splitLine: { show: false },
      axisLabel: {
        showMinLabel: false,
        formatter: (value: number) => Math.abs(value),
        showMaxLabel: false,
        color: getStyleProp('--grey-500'),
        fontWeight: 'bold',
      },
    },
    series: [
      ...series
        .filter((serie) => serieNames.includes(serie.name))
        .map((serie) => ({
          name: serie.name,
          type: 'bar',
          stack: 'one',
          labelLayout: (params) => ({
            x: params.rect.x + 10,
            y: params.rect.y + params.rect.height / 2,
            verticalAlign: 'middle',
            align: 'left',
          }),
          color: serie.color,
          data: serie.values[0].map((val) => ({value: val})),
        })) as SeriesOption[],
      ]
  });
};

export const buildYearOverview = (
  series: EnergyDetailsSeries[],
  dates: string[],
): ((
  serieName: string[],
  resume: { serieName: string; description: string; showTotalMaxValue?: boolean },
  withNegativeSide?: boolean,
) => EChartsOption) => {
  const xAxisData = [
    coreServicesLabels.Jan,
    coreServicesLabels.Feb,
    coreServicesLabels.Mar,
    coreServicesLabels.Apr,
    coreServicesLabels.May,
    coreServicesLabels.Jun,
    coreServicesLabels.Jul,
    coreServicesLabels.Aug,
    coreServicesLabels.Sep,
    coreServicesLabels.Oct,
    coreServicesLabels.Nov,
    coreServicesLabels.Dec,
  ]
  return (serieNames, resume, withNegativeSide = true) => ({
    title: {
      text: `${
        Math.abs(Number(series.find((serie) => serie.name === resume.serieName)?.values.flat()
          .filter((value): value is number => value !== null)
          .reduce((prev, value) => prev + value, 0).toFixed()))
      } kWh - ${resume.description}`,
      left: 75,
      top: 20,
      textStyle: {
        color: getStyleProp('--grey-400'),
        fontSize: 16,
        fontFamily: getStyleProp('--font-family-brand'),
        fontWeight: 500,
      },
      subtextStyle: {
        fontSize: 12,
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      confine: true,
      trigger: 'axis',
      formatter: (elements: any): string => {
        const { dataIndex } = elements[0];
        const month = xAxisData[0 * 12 + dataIndex].split('-').reverse().join('/');

        let template = `
        <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
          <div class="body-3 mr-3">${coreServicesLabels.month}</div>
          <div class="text-mono">${month}</div>
        </div>`;
        for (const element of elements) {
          const {
            seriesName,
            data: { value },
          } = element;
          const { name, color } = telemetryStyles(seriesName);
          if (value !== 0) {
            template += `
            <div class="fx-row ${color} fx-align-center fx-justify-between">
              <div class="body-3 mr-3">${name}</div>
              <div class="text-mono">${Math.abs(value).toFixed(2)} kWh</div>
            </div>`;
          }
        }
        return template;
      },
      backgroundColor: getStyleProp('--grey-100'),
      borderColor: getStyleProp('--grey-100'),
      borderRadius: 4,
      textStyle: {
        color: getStyleProp('--white'),
      },
    },
    xAxis: {
      type : 'category',
      data: xAxisData,
      axisLabel: {
      interval: 0,
      rotate: 40,
      }
    },
    grid: {
      show: false,
      top: '25%',
      bottom: '15%',
      left: '15%',
      right: '5%',
    },
    yAxis: {
      max: ({ max, min }) =>
        Math.abs(max) >= Math.abs(min) ? Math.abs(max) : Math.abs(min),
      min: ({ max, min }) =>
        withNegativeSide
          ? -(Math.abs(max) >= Math.abs(min) ? Math.abs(max) : Math.abs(min))
          : 0,
      type: 'value',
      name: '[kWh]',
      nameLocation: 'end',
      nameTextStyle: {
        color: getStyleProp('--grey-500'),
        fontWeight: 'bold',
      },
      splitLine: { show: false },
      axisLabel: {
        showMinLabel: false,
        formatter: (value: number) => Math.abs(value),
        showMaxLabel: false,
        color: getStyleProp('--grey-500'),
        fontWeight: 'bold',
      },
    },
    series: [
      ...series
        .filter((serie) => serieNames.includes(serie.name))
        .map((serie) => ({
          name: serie.name,
          type: 'bar',
          stack: 'one',
          labelLayout: (params) => ({
            x: params.rect.x + 10,
            y: params.rect.y + params.rect.height / 2,
            verticalAlign: 'middle',
            align: 'left',
          }),
          color: serie.color,
          data: serie.values[0].map((val) => ({value: val})),
        })) as SeriesOption[],
      ]
  });
};
